import React, { useMemo, useState, useEffect } from 'react';
import classes from './DashboardHeader.module.css';
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import plus from '../../../../Assets/plus-icon.png';
import xport from '../../../../Assets/xport-icon.png';
import yesido from '../../../../Assets/yesido.png';
import uploadIcon from '../../../../Assets/uploadicon.png';
import setitup from '../../../../Assets/setitup.png';
import { useUser } from '../../../../UserContext';
import exitIcon from "../../../../Assets/exitIcon.svg";
import warningIconDash from "../../../../Assets/warningIconDash.svg";
import api from '../../../../Components/API/Api';
import swal from 'sweetalert';


export default function DashboardHeader() {
  const { user, isAuthenticated, logout } = useUser();
  const navigate = useNavigate();
  const [showOpeningBalancesModal, setShowOpeningBalancesModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [submittingUpload, setSubmittingUpload] = useState(false);
  const [settingUpAccounts, setSettingUpAccounts] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [accountSettingStatus, setAccountSettingStatus] = useState(null);

  // Debug: Log user data to see what's available
  console.log('DashboardHeader - User data:', user);
  console.log('DashboardHeader - Is authenticated:', isAuthenticated);
  console.log('DashboardHeader - Account Setting Status:', accountSettingStatus);

  useEffect(() => {
    if (showUploadModal) {
      fetchAccounts();
    }
  }, [showUploadModal]);

  // Fetch company info from get-info endpoint to get accurate account_setting_status
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await api.get('/v1/companies/get-info');
        const companyData = Array.isArray(response?.data?.data) ? response.data.data[0] : response?.data?.data;
        
        if (companyData) {
          setCompanyInfo(companyData);
          const status = companyData?.account_setting_status;
          setAccountSettingStatus(status);
          console.log('Fetched account_setting_status from get-info:', status);
        }
      } catch (error) {
        console.error('Error fetching company info from get-info:', error);
        // Fallback to user context if API fails
        setAccountSettingStatus(user?.account_setting_status);
      }
    };

    if (isAuthenticated) {
      fetchCompanyInfo();
    }
  }, [isAuthenticated]);

  const fetchAccounts = async () => {
    setLoadingAccounts(true);
    try {
      // const response = await api.get('/v1/account');
      const response = await api.get('/v1/get-sub-categories');
      // Extract accounts from the API response structure
      const result = Array.isArray(response?.data?.data) ? response.data.data : [];
      setAccounts(result);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  // Prefer active company name when available (keeps header focused on the selected company)
  const companyFromStorage = (() => {
    try {
      const raw = localStorage.getItem('companyInfo');
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  })();

  // Determine active company by matching active id to companies list (from user or localStorage)
  const companyName = useMemo(() => {
    try {
      const companies = user?.companies || (() => {
        const raw = localStorage.getItem('companies');
        return raw ? JSON.parse(raw) : null;
      })() || [];

      const activeId = localStorage.getItem('company_id') || localStorage.getItem('activeCompanyId') || user?.company?.id || user?.company_id || null;
      if (activeId && Array.isArray(companies)) {
        const match = companies.find(c => String(c.id) === String(activeId));
        if (match && match.name) return match.name;
      }

      // fallbacks
      return companyFromStorage?.name || user?.company?.name || user?.company?.company_name || null;
    } catch (e) {
      return companyFromStorage?.name || user?.company?.name || null;
    }
  }, [user, companyFromStorage]);

  // Get user's display name - fallback to 'User' if no name is available
  const displayName = user?.name || user?.firstName || user?.email?.split('@')[0] || 'User';

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleContinueUpload = async () => {
    if (!uploadedFile || !selectedAccount) {
      swal('Error', 'Please select both an account type and a file to upload.', 'error');
      return;
    }

    setSubmittingUpload(true);
    try {
      const companyId = localStorage.getItem('company_id');
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('account_type', selectedAccount);
      formData.append('company_id', companyId);

      const response = await api.post('/v1/upload_account', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Show success notification
      swal('Success', response?.data?.message || 'Chart of accounts uploaded successfully!', 'success');

      console.log('Upload response:', response);
      console.log('UUID from response - data.uuid:', response?.data?.uuid);
      console.log('UUID from response - data.data.uuid:', response?.data?.data?.uuid);

      // Convert uploaded file to data URL for preview
      const reader = new FileReader();
      reader.onload = () => {
        const fileData = {
          name: uploadedFile.name,
          type: uploadedFile.type,
          size: uploadedFile.size,
          data: reader.result,
        };

        // Get UUID from nested data structure
        const uuid = response?.data?.data?.uuid || response?.data?.uuid;
        console.log('Final UUID being passed:', uuid);

        // Close modals and reset state
        setShowUploadModal(false);
        setShowOpeningBalancesModal(false);
        setUploadedFile(null);
        setSelectedAccount('');

        // Redirect to preview page with file and uuid
        navigate('/accounting/chartofaccount-preview', {
          state: {
            files: [fileData],
            uuid: uuid,
          },
        });
      };
      reader.readAsDataURL(uploadedFile);
    } catch (error) {
      // Extract error message from various possible response structures
      let errorMessage = 'An error occurred while uploading the file.';
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // Show error notification
      swal('Upload Failed', errorMessage, 'error');
      console.error('Error uploading account file:', error);
    } finally {
      setSubmittingUpload(false);
    }
  };

  const handleAccountSettingSetup = async (type) => {
    setSettingUpAccounts(true);
    try {
      const companyId = localStorage.getItem('company_id');
      console.log('Calling account_setting endpoint with type:', type, 'company_id:', companyId);
      
      const response = await api.post('/v1/account_setting', {
        type: type.toString(),
        company_id: companyId
      });

      console.log('Account setting response:', response.data);
      
      // For type 0 (existing account), open upload modal without showing alert
      if (type === 0) {
        setShowOpeningBalancesModal(false);
        setShowUploadModal(true);
      } else {
        // For type 1 (setup for me), show success and close modal
        swal('Success', response?.data?.message || 'Account setup initiated successfully!', 'success');
        setShowOpeningBalancesModal(false);
      }
    } catch (error) {
      console.error('Error setting up accounts:', error);
      let errorMessage = 'An error occurred while setting up accounts.';
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      swal('Setup Failed', errorMessage, 'error');
    } finally {
      setSettingUpAccounts(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/v1/download-account-template', { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'chart-of-accounts-template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading template:', error);
    }
  };

  return (
    <>
      <div className={classes.header}>
        <div>
          <h1 className={classes.headertxt}>Dashboard</h1>
          <p className={classes.compnynme}> {companyName ? ` ${companyName}` : 'for today'}.</p>
          <p className={classes.smllittxt}>Welcome back, <span className={classes.displayNameSpan}>{displayName}!</span>  Here's your business overview for today</p>

        </div>
        <div className={classes.btns}>
          <button onClick={() => navigate('/transactions/receivables/sales')} className={classes.btncrte}><img src={plus} className={classes.plusicon} /> Create Sales Invoice</button>
          <button className={classes.btnxprt}><img src={xport} className={classes.xprt} /> Export Report</button>
        </div>
      </div>
      {!(accountSettingStatus === "1" || accountSettingStatus === "2" || accountSettingStatus === 1 || accountSettingStatus === 2) && accountSettingStatus !== null && (
        <div className={classes.setupDiv}>
          <div>
            <img src={warningIconDash} />
          </div>
          <div style={{ flex: 1 }}>
            <div className={classes.setupText}>
              <div>
                <p>Finish Your Accounting Setup</p>
                <h6>Your accounting setup is incomplete. Some accounting features may be unavailable until you complete the required setup steps</h6>
              </div>
              <img src={exitIcon} style={{ width: 24, height: 24, cursor: "pointer" }} />
            </div>
            <div className={classes.completeSetupBut}>
              <button onClick={() => setShowOpeningBalancesModal(true)}> Complete Setup</button>
            </div>
          </div>
        </div>
      )}

      {showOpeningBalancesModal && (
        <div className={classes.modalOverlay}>
          <div className={classes.modalContent}>
            <div className={classes.modalHeadertxt}>
              <h2 className={classes.modalTitle}>Do you have an existing chart of accounts?</h2>
              <p classaccount_setting_statusName={classes.modalDescription}>
                A chart of accounts organises your financial transactions into categories. Let us know how you'd like to proceed so we can get you set up.
              </p>
            </div>


            <div className={classes.optionsContainer} >
              <div className={classes.optionCard} onClick={() => handleAccountSettingSetup(0)} style={{ opacity: settingUpAccounts ? 0.6 : 1, pointerEvents: settingUpAccounts ? 'none' : 'auto', cursor: settingUpAccounts ? 'not-allowed' : 'pointer' }}>
                <div className={classes.optionIcon}>
                  <span>
                    <img src={yesido} style={{ width: 52, height: 52 }} />
                  </span>
                </div>
                <h3>Yes I have one</h3>
                <p>I’ll upload my existing chart of accounts as an exisiting file</p>
                {/* <button className={classes.primaryButton} >
                  Yes, I have opening balances
                </button> */}
              </div>

              <div className={classes.optionCard} onClick={() => handleAccountSettingSetup(1)} style={{ opacity: settingUpAccounts ? 0.6 : 1, pointerEvents: settingUpAccounts ? 'none' : 'auto', cursor: settingUpAccounts ? 'not-allowed' : 'pointer' }}>
                <div className={classes.optionIcon}>
                  <span>
                    <img src={setitup} style={{ width: 52, height: 52 }} />
                  </span>
                </div>
                <h3>Set it up for me</h3>
                <p>Create a default charts of accounts based on selected industry</p>
                {/* <button className={classes.secondaryButton} >
                  Skip for Now
                </button> */}
              </div>
            </div>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div className={classes.modalOverlay}>
          <div className={classes.uploadModalContent}>
            <div className={classes.uploadModalHeader}>
              <h2 className={classes.uploadModalTitle}>Upload your Chart of Accounts</h2>
              <button
                className={classes.closeButton}
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadedFile(null);
                }}
              >
                ✕
              </button>
            </div>

            <p className={classes.uploadDescription}>
              Upload an Excel file (.xlsx or .xls) containing your existing accounts.<br />
              We'll map the columns automatically
            </p>

            <div className={classes.templateDownloadSection}>
              <p className={classes.templateText}>
                Download the <span className={classes.downloadLink} onClick={handleDownloadTemplate}>Charts of Account Template Here</span> Fill in your accounts per type — account code and account name required for each entry
              </p>
            </div>

            <div className={classes.accountSelectSection}>
              {/* <label htmlFor="accountSelect" className={classes.selectLabel}>Select Account</label> */}
              <select
                id="accountSelect"
                className={classes.accountSelect}
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                disabled={loadingAccounts}
              >
                <option value="">
                  {loadingAccounts ? 'Loading accounts...' : 'Select account type'}
                </option>
                {Array.isArray(accounts) && accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.description}
                  </option>
                ))}
              </select>
            </div>
            

            <div className={classes.fileUploadContainer}>
              <input
                type="file"
                id="fileInput"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                className={classes.fileInput}
              />
              <label htmlFor="fileInput" className={classes.fileUploadArea}>
                <div className={classes.fileUploadContent}>
                  <div className={classes.uploadIcon}>
                  <img src={uploadIcon} alt="Upload" style={{ width: 72, height: 72 }} />
                </div>
                <p className={classes.uploadText}>Click to upload or drag and drop</p>
                </div>
                
                <p className={classes.fileTypes}>.xlsx, .xls, .csv accepted</p>
              </label>
              {uploadedFile && (
                <div className={classes.uploadedFileInfo}>
                  <p className={classes.fileName}>✓ {uploadedFile.name}</p>
                </div>
              )}
            </div>

            <div className={classes.uploadButtonsContainer}>
              <button
                className={classes.backButton}
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadedFile(null);
                  setShowOpeningBalancesModal(true);
                }}
              >
                Back
              </button>
              <button
                className={classes.continueButton}
                onClick={handleContinueUpload}
                disabled={!uploadedFile || !selectedAccount || submittingUpload}
              >
                {submittingUpload ? 'Uploading...' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}